import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import StorageProviderService from '@utility/services/storage-provider';
import {
  ALL_DROPDOWN_OPTION,
  ErrorMessageConstant,
} from '@utility/constants/common';
import { CommonUtility } from '@utility/common';
import { ToastMessage } from '@utility/toast';
import { providerKeys } from '../lib/query-keys';

/*
 * Storage providers as other screens look them up: the routing hierarchy, the
 * return centres, the SCM picker, the default provider for velocities. Each
 * keeps the shape its legacy hook had, so the callers did not change.
 */

/* A provider as these lists carry it; the screens read more off `item`. */
export type ProviderRef = {
  _id: string;
  name?: string;
  hierarchy?: number;
  group_name?: string;
  [key: string]: unknown;
};

export type ProviderOption = {
  text: string;
  value: string;
  item: ProviderRef;
};

export type ProviderGroup = {
  id: string;
  title?: string;
  items: (ProviderRef & { id: string })[];
};

/* A shared empty list, so callers that memoize on it see one reference. */
const NO_OPTIONS: ProviderOption[] = [];

const message = (error: Error | null) =>
  error ? error.message || ErrorMessageConstant.defaultAPIError : '';

const toOptions = (rows: ProviderRef[]): ProviderOption[] =>
  rows.map(row => ({ text: row.name ?? '', value: row._id, item: row }));

/** Providers in routing order, grouped under their group name (a provider alone is its own group). */
export function groupByHierarchy(rows: ProviderRef[]): ProviderGroup[] {
  const groups: ProviderGroup[] = [];
  for (const provider of rows) {
    const entry = { ...provider, id: provider._id };
    const id = provider.group_name || provider._id;
    const group = groups.find(existing => existing.id === id);
    if (group && provider.group_name) {
      group.items.push(entry);
    } else {
      groups.push({
        id,
        title: provider.group_name || provider.name,
        items: [entry],
      });
    }
  }
  return groups;
}

/**
 * The region's providers in routing order, grouped or flat. `data` is a copy
 * the screen may reorder before saving, `temp` the copy it resets to; both
 * load fresh on every visit, so a saved order is what comes back.
 */
export function useProviderHierarchy(regionId?: string, grouped = true) {
  const query = useQuery({
    queryKey: providerKeys.hierarchy(regionId ?? '', grouped),
    queryFn: async () => {
      const result = await StorageProviderService.getHierarchy(regionId);
      const rows = ((result?.data ?? []) as ProviderRef[]).sort(
        (a, b) => (a.hierarchy ?? 0) - (b.hierarchy ?? 0),
      );
      return grouped ? groupByHierarchy(rows) : rows;
    },
    enabled: !!regionId,
    gcTime: 0,
  });

  // biome-ignore lint/suspicious/noExplicitAny: the reorder screens mutate these rows freely.
  const [data, setData] = useState<any[]>([]);
  // biome-ignore lint/suspicious/noExplicitAny: as `data`.
  const [temp, setTemp] = useState<any[]>([]);
  useEffect(() => {
    if (!query.data) return;
    setData(query.data);
    setTemp(structuredClone(query.data));
  }, [query.data]);

  return {
    data,
    setData,
    temp,
    setTemp,
    loading: query.isFetching,
    error: message(query.error),
  };
}

/** Where the region's returns can be processed, as select options. */
export function useReturnProcessingCenters(regionId?: string) {
  const query = useQuery({
    queryKey: providerKeys.returnCenters(regionId ?? ''),
    queryFn: async () => {
      const result =
        await StorageProviderService.getReturnProcessingCenter(regionId);
      return toOptions((result?.data ?? []) as ProviderRef[]);
    },
    enabled: !!regionId,
  });

  return {
    data: query.data ?? NO_OPTIONS,
    loading: query.isFetching,
    error: message(query.error),
  };
}

/** The SCM providers as sorted select options, `All` first when asked for. */
export function useProviderOptions(addAll = false) {
  const query = useQuery({
    queryKey: providerKeys.scmOptions(),
    queryFn: async () => {
      const result = await StorageProviderService.scm({});
      return CommonUtility.sortDropdownOptions(
        toOptions((result?.data ?? []) as ProviderRef[]),
      ) as ProviderOption[];
    },
  });

  /* One reference per answer: the order list memoizes its filters on it, and a
     new array each render had it rewrite the URL in a loop. */
  // biome-ignore lint/suspicious/noExplicitAny: legacy pickers take these under their own option types.
  const data = useMemo<any[]>(() => {
    const options = query.data ?? NO_OPTIONS;
    return addAll && query.data ? [ALL_DROPDOWN_OPTION, ...options] : options;
  }, [addAll, query.data]);
  return {
    data,
    loading: query.isFetching,
    error: message(query.error),
  };
}

/**
 * The providers that can be the region's default for velocities, and whether
 * it was set by hand. `manualOverride` is the screen's to flip before saving.
 */
export function useDefaultProviderPredictions(regionId?: string) {
  const query = useQuery({
    queryKey: providerKeys.defaultPredictions(regionId ?? ''),
    queryFn: async () => {
      const result =
        await StorageProviderService.fetchGetFpDefaultPredictions(regionId);
      return {
        providers: (result?.providers ?? null) as ProviderRef[] | null,
        manualOverride: !!result?.manual_override,
      };
    },
    enabled: !!regionId,
  });

  const [manualOverride, setManualOverride] = useState(false);
  useEffect(() => {
    if (query.data) setManualOverride(query.data.manualOverride);
  }, [query.data]);

  useEffect(() => {
    if (query.error) ToastMessage.apiError(query.error);
  }, [query.error]);

  return {
    data: query.data?.providers ?? null,
    loading: query.isFetching,
    manualOverride,
    updateManualOverride: setManualOverride,
  };
}

/** The region's fulfillment threshold settings; `setData` keeps the screen's edits. */
export function useProviderThreshold(regionId?: string) {
  const query = useQuery({
    queryKey: providerKeys.threshold(regionId ?? ''),
    queryFn: () => StorageProviderService.fetchFPThreshold(regionId),
    enabled: !!regionId,
  });

  // biome-ignore lint/suspicious/noExplicitAny: the threshold screen reads and edits the API's body as is.
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    if (query.data !== undefined) setData(query.data);
  }, [query.data]);

  useEffect(() => {
    if (query.error) ToastMessage.apiError(query.error);
  }, [query.error]);

  return { data, setData, loading: query.isFetching };
}

/** Makes a provider the region's default for velocities. */
export function useSetDefaultProvider() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      regionId,
      providerId,
    }: {
      regionId: string;
      providerId: string;
    }) =>
      StorageProviderService.fetchUpdateFpDefaultPredictions(regionId, {
        fulfillment_provider_id: providerId,
      }),
    onSuccess: (_result, { regionId }) => {
      ToastMessage.success('Default Provider Updated Successfully');
      void queryClient.invalidateQueries({
        queryKey: providerKeys.defaultPredictions(regionId),
      });
    },
    onError: error => ToastMessage.apiError(error),
  });

  return {
    loading: mutation.isPending,
    handleAction: (regionId: string, providerId: string) =>
      mutation.mutateAsync({ regionId, providerId }).catch(() => undefined),
  };
}
